/**
 * Skin Analysis Plan – feature flags for radar charts, Ollama descriptions, summary, and routine.
 * Used by Skin Results and Skin Routine pages (skin + kiosk) when the skin analysis flow is selected.
 *
 * Backend Ollama enable/disable: the frontend passes these flags in the API request body. The backend
 * checks and uses only the value from the request when provided (use_ollama_for_routine for
 * /api/salon/generate-routine, use_ollama_for_descriptions for /api/salon/generate-condition-descriptions).
 * When not sent, the backend falls back to its env (USE_OLLAMA_FOR_ROUTINE, USE_OLLAMA_FOR_CONDITION_DESCRIPTIONS).
 * To control Ollama from one place, set the flags below and ensure the frontend always sends them.
 *
 * When any Ollama flag is disabled, the app uses defaults: descriptions = static text from config;
 * summary and routine = backend template-based generation (no Ollama call).
 *
 * Plan presets:
 * - standard: no radar charts.
 * - growth: main radar on, before radar off.
 * - pro:    main + before radar on.
 *
 * Ollama control: In salon, Ollama is now controlled by fetched ai_mode from /summary endpoint.
 * "advanced" => use Ollama, "basic" => use defaults. Plan-based Ollama is deprecated.
 *
 * Plan is fetched from page-setup (external_fields.plan) via useSkinAnalysisPlan() from SkinAnalysisPlanContext.
 * Only the plan from the fetch URL is used; no default plan.
 */

const VALID_PLANS = ['standard', 'growth', 'pro'];

/**
 * Given a plan string from page-setup ('standard' | 'growth' | 'pro'), returns all feature flags.
 * Only valid plan values from the fetch are used; missing or invalid plan yields all flags false.
 */
export function getPlanFlags(plan) {
  const p = plan && VALID_PLANS.includes(String(plan).toLowerCase().trim())
    ? String(plan).toLowerCase().trim()
    : null;
  const ENABLE_RADAR_CHART = p === 'growth' || p === 'pro';
  const ENABLE_BEFORE_RADAR_CHART = p === 'pro';
  // Salon side-capture is controlled by fetched capture_mode, not plan.
  const ENABLE_SIDE_CAPTURE = false;
  // Ollama flags now controlled by fetched ai_mode, not plan. Set to false here to avoid conflicts.
  const ENABLE_OLLAMA_DESCRIPTIONS = false;
  const ENABLE_OLLAMA_SUMMARY = false;
  const ENABLE_OLLAMA_ROUTINE = false;
  return {
    ENABLE_RADAR_CHART,
    ENABLE_BEFORE_RADAR_CHART,
    ENABLE_SIDE_CAPTURE,
    ENABLE_OLLAMA_DESCRIPTIONS,
    ENABLE_OLLAMA_SUMMARY,
    ENABLE_OLLAMA_ROUTINE,
  };
}

// ---- Static exports (no plan = all flags false; use useSkinAnalysisPlan() in app) ----
const _flags = getPlanFlags(null);
export const ENABLE_RADAR_CHART = _flags.ENABLE_RADAR_CHART;
export const ENABLE_BEFORE_RADAR_CHART = _flags.ENABLE_BEFORE_RADAR_CHART;
export const ENABLE_SIDE_CAPTURE = _flags.ENABLE_SIDE_CAPTURE;
export const ENABLE_OLLAMA_DESCRIPTIONS = _flags.ENABLE_OLLAMA_DESCRIPTIONS;
export const ENABLE_OLLAMA_SUMMARY = _flags.ENABLE_OLLAMA_SUMMARY;
export const ENABLE_OLLAMA_ROUTINE = _flags.ENABLE_OLLAMA_ROUTINE;
